import React, { useState, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const API_URL = '/api';

const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  isLoading = false,
  menuWidth = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
   const [isKeyboardNav, setIsKeyboardNav] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, bottom: 'auto' });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // --- HELPER: Find the scrolling parent ---
  const findScrollParent = (element) => {
    if (!element || element === document.body) return document.body;
    const style = window.getComputedStyle(element);
    const isScrollable = 
      style.overflow === 'auto' || style.overflow === 'scroll' || 
      style.overflowY === 'auto' || style.overflowY === 'scroll' ||
      style.overflowX === 'auto' || style.overflowX === 'scroll';
    
    if (isScrollable && (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth)) {
      return element;
    }
    return findScrollParent(element.parentElement);
  };

  // --- Handle Position ---
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const finalWidth = menuWidth ? parseInt(menuWidth) : rect.width;
      setPosition({ top: rect.bottom, left: rect.left, width: finalWidth });
    }
  }, [isOpen, menuWidth]);

  useEffect(() => {
    if (!isOpen) return;
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const finalWidth = menuWidth ? parseInt(menuWidth) : rect.width;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const minRequired = 250;
        
        if (spaceBelow < minRequired && spaceAbove > spaceBelow) {
          setPosition({ top: 'auto', bottom: window.innerHeight - rect.top, left: rect.left, width: finalWidth });
        } else {
          setPosition({ top: rect.bottom, bottom: 'auto', left: rect.left, width: finalWidth });
        }      }
    };
    handleUpdatePosition();
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    const scrollParent = findScrollParent(triggerRef.current);
    if (scrollParent && scrollParent !== document.body) {
      scrollParent.addEventListener('scroll', handleUpdatePosition);
    }
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
      if (scrollParent && scrollParent !== document.body) {
        scrollParent.removeEventListener('scroll', handleUpdatePosition);
      }
    };
  }, [isOpen, menuWidth]);

  // --- FIX: Active Scrolling Logic ---
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0  && isKeyboardNav) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        // CHANGED: 'block: "start"' forces the item to the TOP of the view
        // CHANGED: 'behavior: "auto"' makes it instant (better for keyboard navigation than smooth)
        items[highlightedIndex].scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    }
  }, [highlightedIndex, isOpen, isKeyboardNav]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setSearchTerm('');
      setHighlightedIndex(-1);
 setIsKeyboardNav(false);      setTimeout(() => { searchInputRef.current.focus(); }, 0);
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  const handleKeyDown = (e) => {
    if (!isOpen) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setIsOpen(true); return; } return; }
    switch (e.key) {
     case 'ArrowDown': e.preventDefault(); setIsKeyboardNav(true); setHighlightedIndex(prev => { if (prev < filteredOptions.length - 1) return prev + 1; else if (filteredOptions.length > 0) return 0; return prev; }); break;
      case 'ArrowUp': e.preventDefault(); setIsKeyboardNav(true); setHighlightedIndex(prev => { if (prev > 0) return prev - 1; else if (filteredOptions.length > 0) return filteredOptions.length - 1; return prev; }); break;
      case 'Enter': e.preventDefault(); if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) handleSelectOption(filteredOptions[highlightedIndex]); break;
      case 'Escape': setIsOpen(false); break; default: break;
    }
  };

  
   const handleSelectOption = (option) => { onChange(getOptionValue(option)); setIsOpen(false); setSearchTerm(''); setHighlightedIndex(-1); };
  
  useEffect(() => { 
    const handleClickOutside = (event) => { 
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !event.target.closest('.fixed.z-\\[9999\\]')) {
        setIsOpen(false);
      }
    }; 
    document.addEventListener('mousedown', handleClickOutside); 
    return () => { document.removeEventListener('mousedown', handleClickOutside); }; 
  }, []);
    const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        style={{ borderColor: error ? 'rgb(248, 113, 113)' : 'rgb(230, 217, 204)' }}
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {isLoading && <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2 inline-block"></div>}
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{ 
            top: position.top !== 'auto' ? `${position.top}px` : 'auto', 
            bottom: position.bottom !== 'auto' ? `${position.bottom}px` : 'auto', 
            left: `${position.left}px`, 
            width: `${position.width}px` 
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input ref={searchInputRef} type="text" placeholder="Search..." className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={handleKeyDown} />
          </div>
          <div className="max-h-60 overflow-y-scroll" ref={dropdownListRef}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${highlightedIndex === index ? 'bg-blue-100 text-blue-900' : ''} ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => { setIsKeyboardNav(false); setHighlightedIndex(index); }}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

const DPRFormModal = ({ open, onClose, initialData, onSubmit, onFormSuccess }) => {

  const initialHeader = {
    DPRHeaderId: null,
    DPRNo: '',
    PostingDate: new Date().toISOString().split('T')[0],
    WorkDate: new Date().toISOString().split('T')[0],
    Status: 'Open',
    WorkDonePercent: 0,
    ProjectHeaderId: '',
    ProjectNo: '',
    FeederId: '',
    FeederDescription: '',
    SiteEngineerName: '',
    Circle: '',
    District: '',
    Division: '',
    SubDivision: '',
    SubStation: '',
    ContractorCode: '',
    IsDismental: false,
    SendToBC: false,
    BcSuccess: false
  };

  const initialLine = {
    DPRLineId: null,
    ProjectLineId: '',
    TransactionType: 'Consumption',
    ProductCode: '',
    ProductName: '',
    TaskNo: '',
    ItemCategoryCode: '',
    QuantityForDPR: 0,
    UOM: 'NOS',
    Qty: 0,
    BalanceQty: 0,
    SavedQty: 0, // New field to track original DB quantity for this line
    WorkDate: new Date().toISOString().split('T')[0],
    Remark: '',
    FeederCode: '',
    ContractorName: '',
    BcSuccess: false,
    IsDismental: false,
    ActivityId: null,    // <--- ADD THIS
  SubActivityId: null 
  };

  const [header, setHeader] = useState(initialHeader);
  const [lines, setLines] = useState([{ ...initialLine }]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const [projects, setProjects] = useState([]);
  const [allFeeders, setAllFeeders] = useState([]);
  const [projectFeeders, setProjectFeeders] = useState([]); 
  const [vendors, setVendors] = useState([]);
  const [projectLines, setProjectLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [nextDPRNumber, setNextDPRNumber] = useState('');
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedFeeder, setSelectedFeeder] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState(null);
const [surveyBOQs, setSurveyBOQs] = useState([]);
const [surveyBOQLoading, setSurveyBOQLoading] = useState(false);
  const [productRemainingQtys, setProductRemainingQtys] = useState({});

  const [toast, setToast] = useState({ open: false, type: 'error', title: '', message: '' });
  const [projectActivities, setProjectActivities] = useState([]);

  const [feedersLoading, setFeedersLoading] = useState(false);
  const [projectLinesLoading, setProjectLinesLoading] = useState(false);
  const [initialProductSavedQtys, setInitialProductSavedQtys] = useState({});
   const isHeaderLocked = header.BcSuccess === true || header.BcSuccess === 1;

  const [columnWidths, setColumnWidths] = useState({
    lineNo: 60, projectLine: 300,taskNo: 100, product: 280, transaction: 220, qtyForDpr: 200, qty: 200, balanceQty: 200, uom: 80, workDate: 120, remark: 280, action: 60,activity: 200,      // <--- ADD THIS
  subActivity: 200
  });
  const resizingRef = useRef(null);

 useEffect(() => {
  const initializeData = async () => {
    if (open) {
      setIsMaximized(false);
      resetForm();
      
      const fetchedProjects = await fetchProjects();
      const fetchedVendors = await fetchVendors();
      fetchProducts();

      const isEditMode = initialData && initialData.header && initialData.header.DPRHeaderId;
      if (!isEditMode) {
        fetchNextDPRNumber();
      }

      if (initialData) {
        const sourceData = initialData.header ? initialData.header : initialData;
        setHeader(prev => ({
          ...initialHeader,
          ...sourceData,
          IsDismental: sourceData.IsDismental === true || sourceData.IsDismental === 1 || sourceData.IsDismental === 'true',
          PostingDate: sourceData.PostingDate ? sourceData.PostingDate.split('T')[0] : new Date().toISOString().split('T')[0],
          WorkDate: sourceData.WorkDate ? sourceData.WorkDate.split('T')[0] : new Date().toISOString().split('T')[0]
        }));

        if (sourceData.ContractorCode) {
          const vendor = fetchedVendors.find(v => v.vendor_code == sourceData.ContractorCode);
          if (vendor) setSelectedContractor(vendor);
        }

        if (sourceData.ProjectNo) {
  const project = fetchedProjects.find(p => p.No == sourceData.ProjectNo);

  if (project) {
    setSelectedProject(project);

    const fetchedFeeders = await fetchProjectFeeders(sourceData.ProjectNo);
    await fetchActivities(sourceData.ProjectNo);

    if (sourceData.FeederId && fetchedFeeders) {
      const feeder = fetchedFeeders.find(f => f.FeederId == sourceData.FeederId);

      if (feeder) {
        setSelectedFeeder(feeder);

        // 1. Fetch BOQs
        await fetchSurveyBOQ(sourceData.ProjectNo, feeder.Code);
        
        // 2. ✅ SMART FIX: Ensure saved BOQ is in the list (Prevent Duplicates)
        if (sourceData.SurveyBOQID) {
          setSurveyBOQs(prevBOQs => {
            // Check if the ID exists (strict comparison)
            // We also check ID property just in case
            const exists = prevBOQs.some(b => 
              b.SurveyBOQID == sourceData.SurveyBOQID || 
              b.ID == sourceData.SurveyBOQID
            );
            
            if (!exists) {
              // If missing, add it
              return [
                ...prevBOQs,
                {
                  SurveyBOQID: sourceData.SurveyBOQID,
                  SurveyBOQNo: sourceData.SurveyBOQNo || `BOQ ID: ${sourceData.SurveyBOQID}`
                }
              ];
            }
            
            // If it exists, return the list as is
            return prevBOQs;
          });
        }
      }
    }
  }
}

        // --- FIX: Define fetchedProjectLines in the outer scope ---
        let fetchedProjectLines = null;
        
        if (sourceData.ProjectHeaderId && sourceData.FeederId && sourceData.ContractorCode) {
          fetchedProjectLines = await fetchProjectLines(sourceData.ProjectHeaderId, sourceData.FeederId,sourceData.ContractorCode);
        }
        
        if (initialData.lines && initialData.lines.length > 0) {
         const savedMap = {};
          initialData.lines.forEach(l => {
            const pc = l.ProductCode;
            const tn = l.TaskNo || '';
            const actId = l.ActivityId || '';
            const subActId = l.SubActivityId || '';
            // Use 4-part composite key
            savedMap[`${pc}_${tn}_${actId}_${subActId}`] = (savedMap[`${pc}_${tn}_${actId}_${subActId}`] || 0) + (l.Qty || 0);
          });
          setInitialProductSavedQtys(savedMap);

          const editLines = initialData.lines.map(l => {
            // FIX: Safely access fetchedProjectLines (it might be null if project/feeder missing)
            const productDetail = fetchedProjectLines?.find(pl => pl.ProductCode === l.ProductCode && pl.JobTaskNo === l.TaskNo); // Matc
            return {
              ...initialLine,
              ...l,
              TaskNo: l.TaskNo || productDetail?.JobTaskNo || '',
              IsDismental: productDetail?.IsDismental === true || productDetail?.IsDismental === 1,
              SavedQty: l.Qty || 0,
              WorkDate: l.WorkDate ? l.WorkDate.split('T')[0] : new Date().toISOString().split('T')[0]
            };
          });
          setLines(editLines);
          
          if (fetchedProjectLines && fetchedProjectLines.length > 0) {
            //  recalculateUsedQtys(editLines, fetchedProjectLines, savedMap);
          }
        }
      }
    }
  };
  initializeData();
}, [open, initialData]);

  const fetchNextDPRNumber = async () => {
    try {
      const res = await fetch(`${API_URL}/dpr/next-number`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await res.json();
      if (data && data.nextDPRNo) setNextDPRNumber(data.nextDPRNo);
    } catch (e) { setNextDPRNumber('DPR-P-0001'); }
  };
const fetchSurveyBOQ = async (projectNo, feederCode) => {
  if (!projectNo || !feederCode) return;

  setSurveyBOQLoading(true);

  try {
    const token = localStorage.getItem('authToken');

    const res = await fetch(
      `${API_URL}/surveyboq-for-dpr?projectNo=${projectNo}&feederCode=${encodeURIComponent(feederCode)}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const data = await res.json();

    if (data.success) {
      // ✅ FIX: Normalize data to ensure SurveyBOQID exists
      // This handles cases where API returns 'ID' or 'SurveyBOQID'
      const normalizedData = (data.data || []).map(item => ({
        ...item,
        SurveyBOQID: item.SurveyBOQID || item.ID || item.SurveyBOQId // Fallback checks
      }));

      setSurveyBOQs(normalizedData);
    } else {
      setSurveyBOQs([]);
    }
  } catch (e) {
    console.error("Error fetching BOQ:", e);
    setSurveyBOQs([]);
  } finally {
    setSurveyBOQLoading(false);
  }
};
   const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : []; // Return data for immediate use
    } catch (e) { setProjects([]); return []; }
  };

    const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/vendors`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : []; // Return data for immediate use
    } catch (e) { setVendors([]); return []; }
  };
  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/products`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) { setProducts([]); }
  };

  const fetchProjectFeeders = async (projectNo) => {
    setFeedersLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project/${projectNo}/feeders-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setProjectFeeders(Array.isArray(data) ? data : []);
      return data;
    } catch (e) {
      console.error('Error fetching project feeders:', e);
      setProjectFeeders([]);
      return [];
    } finally {
      setFeedersLoading(false);
    }
  };

  const fetchActivities = async (projectNo) => {
  if (!projectNo) {
    setProjectActivities([]);
    return;
  }
  try {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_URL}/activity-master/project/${projectNo}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch activities');
    const data = await res.json();
    setProjectActivities(data || []);
  } catch (e) {
    console.error("Error fetching activities:", e);
    setProjectActivities([]);
  }
};

  const fetchProjectLines = async (projectHeaderId, feederId,vendorCode) => {
    setProjectLinesLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const url = `${API_URL}/project/${projectHeaderId}/feeder/${feederId}/lines?vendorCode=${vendorCode || ''}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      setProjectLines(Array.isArray(data) ? data : []);
      return data; // Return data for immediate use
    } catch (e) {
      console.error('Error fetching project lines:', e);
      setProjectLines([]);
      return [];
    } finally {
      setProjectLinesLoading(false);
    }
  };

  const fetchRemainingQtys = async (projectHeaderId, feederId,vendorCode) => {
    try {
      const token = localStorage.getItem('authToken');
       const url = `${API_URL}/dpr/project/${projectHeaderId}/feeder/${feederId}/remaining-qty?vendorCode=${vendorCode || ''}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        const qtyMap = {};
        data.data.forEach(item => {
          qtyMap[item.ProductCode] = {
            totalReceived: item.TotalReceivedQty,
            totalDPR: item.TotalDPRQty,
            remaining: item.RemainingQty
          };
        });
        setProductRemainingQtys(qtyMap);
      }
    } catch (e) {
      console.error('Error fetching remaining qtys:', e);
      setProductRemainingQtys({});
    }
  };

  const filteredProjectLines = useMemo(() => {
    // 1. If project lines haven't loaded yet, return empty
    if (!projectLines || projectLines.length === 0) return [];

    // 2. Create a Set of ProjectLineIds that are currently in the document (for Edit Mode)
    const savedProjectLineIds = new Set(
      (initialData?.lines || []).map(l => l.ProjectLineId)
    );

    // 3. Filter the list
    return projectLines.filter(pl => {
      
      // --- NEW LOGIC: If Header "Is Dismental" is checked, show EVERYTHING ---
      if (header.IsDismental) {
        // Condition A: Show if the item is dismantlable (Standard logic)
        const isDismantlable = pl.IsDismental === true || pl.IsDismental === 1;
        
        // Condition B: Show if the item is already saved in the document (Fix for your issue)
        const isSavedLine = savedProjectLineIds.has(pl.ProjectLineId);

        // Return items that are EITHER dismantlable OR already saved
        return isDismantlable || isSavedLine;
      }

      // --- EXISTING LOGIC: Only apply qty checks if Dismental is NOT checked ---
      const remaining = pl.RemainingQty || 0;
      const isSavedLine = savedProjectLineIds.has(pl.ProjectLineId);

      // Show if: (Remaining Qty > 0) OR (It is a saved line being edited)
      return remaining > 0 || isSavedLine;
    });
    // IMPORTANT: Add 'header.IsDismental' to the dependency array
  }, [projectLines, initialData, header.IsDismental]);
  
  const handleContractorChange = async (vendorCode) => {
    // 1. Find the vendor object
    const vendor = vendors.find(v => v.vendor_code == vendorCode);
    
    // 2. Update State
    setSelectedContractor(vendor || null);
    setHeader(prev => ({ ...prev, ContractorCode: vendorCode }));

    // 3. If Project and Feeder are selected, refresh lines for this Vendor
    if (header.ProjectHeaderId && header.FeederId) {
       await fetchProjectLines(header.ProjectHeaderId, header.FeederId, vendorCode);
       await fetchRemainingQtys(header.ProjectHeaderId, header.FeederId, vendorCode);
    }
  };

  const handleProjectChange = async (projectNo) => {
    const project = projects.find(p => p.No == projectNo);
    setSelectedProject(project || null);
    
    setSelectedFeeder(null);
    setProjectFeeders([]);
    setProjectLines([]);
    setLines([{ ...initialLine }]);
    setProductRemainingQtys({});

    setHeader(prev => ({ 
      ...prev, 
      ProjectNo: projectNo,
      ProjectHeaderId: project?.ProjectHeaderId || '',
      FeederId: ''
    }));

    if (projectNo) {
      await fetchProjectFeeders(projectNo);
      await fetchActivities(projectNo);
    }
  };

  const handleFeederChange = async (feederId) => {
  const feeder = projectFeeders.find(f => f.FeederId == feederId);
  setSelectedFeeder(feeder || null);

  setLines([{ ...initialLine }]);
  setProductRemainingQtys({});
  setProjectLines([]);
  setSurveyBOQs([]); // reset BOQ

  setHeader(prev => ({ 
    ...prev, 
    FeederId: feederId,
    SurveyBOQID: null ,
    FeederDescription: feeder?.Description || '',
    SiteEngineerName: feeder?.SiteEngineerName || '',
    Circle: feeder?.Circle || '',
    District: feeder?.District || '',
    Division: feeder?.Division || '',
    SubDivision: feeder?.SubDivision || '',
    SubStation: feeder?.SubStation || ''
  }));

  if (header.ProjectHeaderId && feederId) {
    await fetchProjectLines(header.ProjectHeaderId, feederId, header.ContractorCode);

    // 🔥 CALL BOQ API HERE
    await fetchSurveyBOQ(header.ProjectNo, feeder?.Code);
  }
};

  const handleProjectLineChange = (index, projectLineId) => {
    const projectLine = projectLines.find(pl => pl.ProjectLineId == projectLineId);
    const newLines = [...lines];

    if (projectLine) {
      const productCode = projectLine.ProductCode || '';
      const isProductDismantlable = projectLine.IsDismental === true || projectLine.IsDismental === 1;

      let currentTransactionType = newLines[index].TransactionType;
      
      if (currentTransactionType === 'Dismental' && !isProductDismantlable) {
        showToast('This product does not allow Dismental. Transaction type reset to Consumption.', 'error', 'Invalid Product');
        currentTransactionType = 'Consumption';
      }

      newLines[index] = {
        ...newLines[index],
        ProjectLineId: projectLineId,
        ProductCode: productCode,
        ProductName: projectLine.ProductName || projectLine.Description || '',
        ItemCategoryCode: projectLine.ItemCategory || '',
        UOM: projectLine.UnitOfMeasureCode || 'NOS',
        QuantityForDPR: 0,
        BalanceQty: 0,
        Qty: 0,
        SavedQty: 0,
        TaskNo: projectLine.JobTaskNo || '', // <--- ADD THIS: Capture TaskNo
        FeederCode: selectedFeeder?.Code || '',
        ContractorName: selectedContractor?.vendor_name || '',
        WorkDate: header.WorkDate || new Date().toISOString().split('T')[0],
        IsDismental: isProductDismantlable,
        TransactionType: currentTransactionType
      };
    }
    
    setLines(newLines);
    recalculateUsedQtys(newLines);
  };

  const handleTransactionTypeChange = (index, transactionType) => {
  const newLines = [...lines];
  const line = newLines[index];

  // Validation: Check if product allows dismental
  if (transactionType === 'Dismental' && !line.IsDismental) {
    showToast('This product does not allow Dismental.', 'error', 'Invalid Selection');
    return; // Stop execution
  }

  newLines[index].TransactionType = transactionType;

  // --- NEW LOGIC: If Dismental, force Avail and Balance to 0 ---
    if (transactionType === 'Dismental') {
      newLines[index].QuantityForDPR = 0;
      newLines[index].BalanceQty = 0;
    }

  setLines(newLines);
};

  const handleQtyChange = (index, qty) => {
    const newLines = [...lines];
    let newVal = parseFloat(qty) || 0;
    
    const line = newLines[index];

    // --- CASE 1: DISMENTAL ---
    if (line.TransactionType === 'Dismental') {
      // 1. Prevent negative numbers
      if (newVal < 0) newVal = 0;

      // 2. Set the Qty directly
      newLines[index].Qty = newVal;

      // 3. Calculate Balance based purely on the user-entered "Available Qty"
      // We do NOT check the database (projectLines) here.
       newLines[index].QuantityForDPR = 0;
      newLines[index].BalanceQty = 0;

      // 4. Update state and stop here (Do not run DB checks)
      setLines(newLines);
      recalculateUsedQtys(newLines); 
      return;
    }

        // --- CASE 2: CONSUMPTION / DAMAGE (Existing Logic) ---
    const productCode = line.ProductCode;
    const taskNo = line.TaskNo || '';
    const activityId = line.ActivityId || '';
    const subActivityId = line.SubActivityId || '';

    // NEW: Validate Activity and SubActivity are selected
    if (!activityId || !subActivityId) {
      showToast('Please select Activity and Sub-Activity before entering quantity.', 'error', 'Required Fields');
      newLines[index].Qty = 0;
      setLines(newLines);
      return;
    }

    // 1. Find the matching project line from Database (with Activity/SubActivity)
    const projectLineData = projectLines.find(pl => 
      (pl.ProductCode || pl.No) === productCode && 
      (pl.JobTaskNo || '') === taskNo &&
      pl.ActivityId == activityId &&
      pl.SubActivityId == subActivityId
    );

    const baseRemaining = projectLineData?.RemainingQty || 0;

    // 2. Lookup saved qty using the COMPOSITE KEY (4 parts now)
    const totalDocumentSavedQty = initialProductSavedQtys[`${productCode}_${taskNo}_${activityId}_${subActivityId}`] || 0;

    const totalPoolAvailable = baseRemaining + totalDocumentSavedQty;

    // 3. Calculate used before this line (match all 4 keys)
    let usedBeforeThis = 0;
    for (let i = 0; i < index; i++) {
      if (
        newLines[i].ProductCode === productCode && 
        (newLines[i].TaskNo || '') === taskNo &&
        (newLines[i].ActivityId || '') === activityId &&
        (newLines[i].SubActivityId || '') === subActivityId &&
        newLines[i].TransactionType !== 'Dismental'
      ) {
        usedBeforeThis += (newLines[i].Qty || 0);
      }
    }

    const availableForThisLine = totalPoolAvailable - usedBeforeThis;

    // 4. Validate against Database Stock
    if (newVal > availableForThisLine) {
      showToast(
        `Cannot enter more than available quantity. Available: ${availableForThisLine.toFixed(6)}`,
        'error',
        'Quantity Exceeded'
      );
      newVal = availableForThisLine < 0 ? 0 : availableForThisLine;
    }

    if (newVal < 0) newVal = 0;

    newLines[index].Qty = newVal;
    setLines(newLines);
    recalculateUsedQtys(newLines);
  };

  // Updated recalculateUsedQtys to accept optional loadedProjectLines
  // Update the function to accept savedQtysMap (optional parameter)
const recalculateUsedQtys = (currentLines, loadedProjectLines = null, savedQtysMap = null) => {
  const sourceProjectLines = loadedProjectLines || projectLines;
  const currentSavedQtys = savedQtysMap || initialProductSavedQtys;

  const productLinesMap = {};

  currentLines.forEach((line, idx) => {
    const pc = line.ProductCode;
    const tn = line.TaskNo || '';
    const activityId = line.ActivityId || '';
    const subActivityId = line.SubActivityId || '';

    if (pc) {
      const key = `${pc}_${tn}_${activityId}_${subActivityId}`;
      if (!productLinesMap[key]) productLinesMap[key] = [];
      productLinesMap[key].push({ index: idx, qty: line.Qty || 0, transactionType: line.TransactionType });
    }
  });

  Object.keys(productLinesMap).forEach(key => {
    const [productCode, taskNo, activityId, subActivityId] = key.split('_');

    if (!activityId || !subActivityId) {
      productLinesMap[key].forEach((item) => {
        if (item.transactionType !== 'Dismental') {
          currentLines[item.index].QuantityForDPR = 0;
          currentLines[item.index].BalanceQty = 0;
        }
      });
      return;
    }

    const projectLineData = sourceProjectLines.find(pl =>
      (pl.ProductCode || pl.No) === productCode &&
      pl.JobTaskNo === taskNo &&
      pl.ActivityId == activityId &&
      pl.SubActivityId == subActivityId
    );

    const baseRemaining = projectLineData?.RemainingQty || 0;
    const totalDocumentSavedQty = currentSavedQtys[key] || 0;

    let runningAvailable = baseRemaining + totalDocumentSavedQty;

    productLinesMap[key].forEach((item) => {
      const lineIdx = item.index;
      const line = currentLines[lineIdx];

      if (item.transactionType === 'Dismental') {
        currentLines[lineIdx].QuantityForDPR = 0;
        currentLines[lineIdx].BalanceQty = 0;
        return;
      }

      // ✅ KEY FIX: If line is saved (has DPRLineId), keep its saved QuantityForDPR
      // Only recalculate for new lines (no DPRLineId)
      if (line.DPRLineId) {
        // Just update the running pool by subtracting this line's qty
        // so next lines get correct available
        runningAvailable = runningAvailable - item.qty;
        runningAvailable = runningAvailable < 0 ? 0 : parseFloat(runningAvailable.toFixed(6));
      } else {
        // New line: calculate fresh
        currentLines[lineIdx].QuantityForDPR = runningAvailable;
        const balance = runningAvailable - item.qty;
        currentLines[lineIdx].BalanceQty = parseFloat(balance.toFixed(6));
        runningAvailable = balance < 0 ? 0 : parseFloat(balance.toFixed(6));
      }
    });
  });

  setLines([...currentLines]);
};

  const addLine = () => {
    const newLine = { 
      ...initialLine, 
      WorkDate: header.WorkDate || new Date().toISOString().split('T')[0],
      FeederCode: selectedFeeder?.Code || '',
      ContractorName: selectedContractor?.vendor_name || ''
    };
    const newLines = [...lines, newLine];
    setLines(newLines);
    recalculateUsedQtys(newLines);
  };

  const removeLine = (index) => {
    if (lines.length > 1) {
      const newLines = lines.filter((_, i) => i !== index);
      setLines(newLines);
      recalculateUsedQtys(newLines);
    }
  };

const handleLineChange = (index, field, value) => {
  const newLines = [...lines];
  if (field === 'ActivityId') {
    newLines[index].SubActivityId = null;
  }
  newLines[index][field] = value;
  setLines(newLines);
  
  // Trigger recalculation when Activity or SubActivity changes
  if (field === 'ActivityId' || field === 'SubActivityId') {
    recalculateUsedQtys(newLines);
  }
};

  const resetForm = () => {
    setHeader(initialHeader);
    setLines([{ ...initialLine }]);
    setNextDPRNumber('');
    setSelectedProject(null);
    setSelectedFeeder(null);
    setSelectedContractor(null);
    setProductRemainingQtys({});
    setProjectLines([]);
    setProjectFeeders([]);
  };

  const startResize = (columnKey, startX) => {
    resizingRef.current = { columnKey, startX, startWidth: columnWidths[columnKey] };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;
    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;
    setColumnWidths(prev => ({ ...prev, [columnKey]: Math.max(60, startWidth + delta) }));
  };

  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!header.ContractorCode) {
      showToast('Contractor is required.', 'error', 'Required Field');
      return;
    }
    if (!header.WorkDonePercent || parseFloat(header.WorkDonePercent) === 0) {
      showToast('Work Done % should not be zero.', 'error', 'Validation Error');
      return;
    }
    if (!header.SurveyBOQID || parseFloat(header.SurveyBOQID) === 0) {
      showToast('Survey BOQ No is required.', 'error', 'Validation Error');
      return;
    }

    if (!header.ProjectNo) {
      showToast('Project is required.', 'error', 'Required Field');
      return;
    }

    if (!header.FeederId) {
      showToast('Feeder is required.', 'error', 'Required Field');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');

      const processedLines = lines.map(l => ({
        ...l,
        ProjectLineId: l.ProjectLineId ? parseInt(l.ProjectLineId) : null,
        QuantityForDPR: parseFloat(l.QuantityForDPR) || 0,
        Qty: parseFloat(l.Qty) || 0,
        BalanceQty: parseFloat(l.BalanceQty) || 0,
        FeederCode: selectedFeeder?.Code || l.FeederCode,
        ContractorName: selectedContractor?.vendor_name || l.ContractorName
      }));

      const payload = {
        header: {
          ...header,
          DPRNo: (initialData && initialData.header && initialData.header.DPRHeaderId) ? header.DPRNo : nextDPRNumber,
          ProjectHeaderId: header.ProjectHeaderId ? parseInt(header.ProjectHeaderId) : null,
          FeederId: header.FeederId ? parseInt(header.FeederId) : null,
          SurveyBOQID: header.SurveyBOQID ? parseInt(header.SurveyBOQID) : null, 
          WorkDonePercent: parseFloat(header.WorkDonePercent) || 0
        },
        lines: processedLines
      };

      const sourceData = initialData && initialData.header ? initialData.header : initialData;
      const editId = sourceData ? sourceData.DPRHeaderId : null;

      const isEditMode = !!editId;
      const url = isEditMode ? `${API_URL}/dpr/${editId}` : `${API_URL}/dpr`;
      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        // CHANGE: Prioritize errorData.error which contains the specific message from backend
        throw new Error(errorData.error || errorData.message || 'Failed to save data.');
      }

      const result = await res.json();
      if (onFormSuccess) {
        onFormSuccess(result, isEditMode ? 'Updated' : 'Created');
      }
      onClose();

    } catch (error) {
      showToast(error.message, 'error', 'Save Error');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const showToast = (message, type = 'error', title = 'Validation Error', duration = 4000) => {
    setToast({ open: true, message, type, title });
    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };

  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th style={{ width }} className={`relative p-2 ${align === 'right' ? 'text-right' : ''} select-none`}>
      {children}
      <div className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400" onMouseDown={(e) => onResize(e.clientX)} />
    </th>
  );

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  if (!open) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            button:focus, select:focus, input:focus, textarea:focus { outline: 2px solid #4299e1; outline-offset: 2px; }
            input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
            input[type=number] { -moz-appearance: textfield; }
            .uppercase-input { text-transform: uppercase; }
            .resizable-table { table-layout: fixed; min-width: max-content; }
            @keyframes toastIn { from { opacity: 0; transform: translate(-50%, -16px) scale(0.98); } to { opacity: 1; transform: translate(-50%, 0) scale(1); } }
            @keyframes toastProgress { from { width: 100%; } to { width: 0%; } }
            .animate-toastIn { animation: toastIn 0.25s ease-out; }
            .animate-toastProgress { animation: toastProgress 4s linear forwards; }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'h-full max-w-full' : 'h-full max-w-7xl'}`} tabIndex={0}>

          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">
                {(initialData && initialData.header && initialData.header.DPRHeaderId) ? 'Edit DPR' : 'Create DPR'}
              </h2>
              <div className="flex items-center gap-2">
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">

              {/* HEADER SECTION */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">DPR Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">DPR No</label>
                    <input type="text" value={(initialData && initialData.header) ? header.DPRNo : nextDPRNumber} disabled className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 font-medium" />
                  </div>

                  {/* REPLACE THIS BLOCK */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Posting Date</label>
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={header.PostingDate ? dayjs(header.PostingDate) : null}
      onChange={(newValue) => {
        setHeader({
          ...header,
          PostingDate: newValue ? newValue.format('YYYY-MM-DD') : ''
        });
      }}
      format="DD/MM/YYYY"
      slotProps={{
        textField: {
          size: 'small',
          className: 'w-full'
        }
      }}
    />
  </LocalizationProvider>
</div>

                  {/* REPLACE THIS BLOCK */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Work Date</label>
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={header.WorkDate ? dayjs(header.WorkDate) : null}
      onChange={(newValue) => {
        setHeader({
          ...header,
          WorkDate: newValue ? newValue.format('YYYY-MM-DD') : ''
        });
      }}
      format="DD/MM/YYYY"
      slotProps={{
        textField: {
          size: 'small',
          className: 'w-full'
        }
      }}
    />
  </LocalizationProvider>
</div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Work Done % <span className='text-red-600'>*</span></label>
                    <input type="number" step="0.01" min="0" max="100" value={header.WorkDonePercent} 
                    disabled={isHeaderLocked}
                    onChange={e => setHeader({ ...header, WorkDonePercent: e.target.value })} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Contractor <span className='text-red-600'>*</span></label>
                    <SearchableDropdown
                      options={vendors}
                      value={header.ContractorCode}
                      disabled={isHeaderLocked}
                      onChange={handleContractorChange}
                      placeholder="Select Contractor..."
                      getOptionLabel={(v) => v.vendor_name || v.name || ''}
                      getOptionValue={(v) => v.vendor_code || v.code || ''}
                      getOptionSecondaryLabel={(v) => v.vendor_code || v.code || ''}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project <span className='text-red-600'>*</span></label>
                    <SearchableDropdown
                      options={projects}
                      value={header.ProjectNo}
                      disabled={isHeaderLocked}
                      onChange={handleProjectChange}
                      placeholder="Select Project..."
                      getOptionLabel={(p) => `${p.No} - ${p.Description || ''}`}
                      getOptionValue={(p) => p.No}
                      getOptionSecondaryLabel={(p) => p.No}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Feeder <span className='text-red-600'>*</span></label>
                    <SearchableDropdown
                      options={projectFeeders}
                      value={header.FeederId}
                      onChange={handleFeederChange}
                      placeholder={!header.ProjectNo ? "Select Project first..." : "Select Feeder..."}
                      disabled={isHeaderLocked || !header.ProjectNo}
                      isLoading={feedersLoading}
                      getOptionLabel={(f) => `${f.Code} - ${f.Description || ''}`}
                      getOptionValue={(f) => f.FeederId}
                      getOptionSecondaryLabel={(f) => f.Code}
                    />
                  </div>
                 <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Survey BOQ <span className='text-red-600'>*</span></label>
  
  {/* Updated: Always show dropdown in both Create and Edit mode */}
  <SearchableDropdown
    options={surveyBOQs}
    value={header.SurveyBOQID}
    onChange={(val) =>
      setHeader(prev => ({
        ...prev,
        SurveyBOQID: typeof val === 'object' ? val.SurveyBOQID : val
      }))
    }
    placeholder={
      !header.FeederId
        ? "Select Feeder first..."
        : surveyBOQLoading
        ? "Loading BOQ..."
        : "Select Survey BOQ..."
    }
    // Disable if Feeder not selected, Loading, OR Header is locked (BcSuccess=true)
    disabled={!header.FeederId || surveyBOQLoading || isHeaderLocked}
    isLoading={surveyBOQLoading}
    getOptionLabel={(b) => b.SurveyBOQNo}
    getOptionValue={(b) => b.SurveyBOQID}
    getOptionSecondaryLabel={(b) => b.SurveyBOQNo}
  />
</div>
                  {selectedContractor && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Contractor Name</label>
                      <input type="text" value={selectedContractor.vendor_name || ''} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                    </div>
                  )}
                   
                  {selectedProject && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Project Description</label>
                      <input type="text" value={selectedProject.Description || ''} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                    </div>
                  )}

                 
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Feeder Description</label>
                      <input 
                        type="text" 
                        value={
                          (initialData && initialData.header && initialData.header.DPRHeaderId)
                            ? header.FeederDescription                    // EDIT MODE: Show saved DB data
                            : selectedFeeder?.Description || ''            // CREATE MODE: Show live selection
                        } 
                        readOnly 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" 
                      />
                    </div>
                  

                
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Site Engineer</label>
                      <input 
                        type="text" 
                        value={
                          (initialData && initialData.header && initialData.header.DPRHeaderId)
                            ? header.SiteEngineerName                     // EDIT MODE: Show saved DB data
                            : selectedFeeder?.SiteEngineerName || ''       // CREATE MODE: Show live selection
                        } 
                        readOnly 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" 
                      />
                    </div>
                  

                 
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <input 
                      type="text" 
                      value={
                        (initialData && initialData.header && initialData.header.DPRHeaderId)
                          ? header.Circle                              // EDIT MODE: Show saved DB data
                          : selectedFeeder?.Circle || ''                // CREATE MODE: Show live selection
                      } 
                      readOnly 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                    />
                  </div>
                

                  
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">District</label>
                      <input 
                        type="text" 
                        value={
                          (initialData && initialData.header && initialData.header.DPRHeaderId)
                            ? header.District                             // EDIT MODE: Show saved DB data
                            : selectedFeeder?.District || ''              // CREATE MODE: Show live selection
                        } 
                        readOnly 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                      />
                    </div>
                  

                 
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Division</label>
                      <input 
                        type="text" 
                        value={
                          (initialData && initialData.header && initialData.header.DPRHeaderId)
                            ? header.Division                            // EDIT MODE: Show saved DB data
                            : selectedFeeder?.Division || ''             // CREATE MODE: Show live selection
                        } 
                        readOnly 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                      />
                    </div>
                  

                 
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                      <input 
                        type="text" 
                        value={
                          (initialData && initialData.header && initialData.header.DPRHeaderId)
                            ? header.SubDivision                          // EDIT MODE: Show saved DB data
                            : selectedFeeder?.SubDivision || ''           // CREATE MODE: Show live selection
                        } 
                        readOnly 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                      />
                    </div>
                  

          
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                      <input 
                        type="text" 
                        value={
                          (initialData && initialData.header && initialData.header.DPRHeaderId)
                            ? header.SubStation                           // EDIT MODE: Show saved DB data
                            : selectedFeeder?.SubStation || ''            // CREATE MODE: Show live selection
                        } 
                        readOnly 
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                      />
                    </div>
                  


                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Is Dismental</label>
                    <div className="flex items-center h-10">
                      <input type="checkbox" disabled={isHeaderLocked} checked={header.IsDismental} onChange={e => setHeader({ ...header, IsDismental: e.target.checked })} className="w-5 h-5 rounded border-gray-300 text-[#B88A5A] focus:ring-[#B88A5A]" />
                    </div>
                  </div>

                  {/* --- REPLACE THIS BLOCK INSIDE THE DPR INFORMATION GRID --- */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]"> Status</label>
  <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 shadow-sm min-h-[42px] flex items-center">
    {(header.IsPosted === 1 || header.IsPosted === true) ? (
      <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
        DPR Posted Successfully
      </span>
    ) : (header.SendToBC === 1 || header.SendToBC === true) ? (
      <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">
        Sent to BC (Ready to Post)
      </span>
    ) : header.ApprovalStatus === 'PENDING' ? (
      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
        Pending Approval (Level {header.CurrentLevel || 1})
      </span>
    ) : header.ApprovalStatus === 'REJECTED' ? (
      <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
        Rejected
      </span>
    ) : header.ApprovalStatus === 'APPROVED' ? (
      <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
        Approved (Ready to Send BC)
      </span>
    ) : (
      <span className="text-xs text-gray-500 italic">Open</span>
    )}
  </div>
</div>
                </div>
              </div>

              {/* LINES SECTION */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">DPR Lines</h3>
                  <button 
                    type="button" 
                    onClick={addLine} 
                   disabled={isHeaderLocked || !header.ProjectNo || !header.FeederId}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
                      (!header.ProjectNo || !header.FeederId) 
                        ? 'text-gray-400 border-gray-300 cursor-not-allowed' 
                        : 'text-[#B88A5A] border-[#B88A5A] hover:bg-[#B88A5A] hover:text-white'
                    }`}
                  >
                    <PlusIcon size={16} /> Add Line
                  </button>
                </div>

                {!header.ProjectNo || !header.FeederId ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    Please select Project and Feeder to view project line items.
                  </div>
                ) : (
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="resizable-table border border-[#E2E8F0] w-full">
                      <thead>
                        <tr className="bg-[#BDDDFC] text-[#384959]">
                          <ResizableTh width={columnWidths.lineNo} onResize={(x) => startResize('lineNo', x)} align="center">#</ResizableTh>
                          <ResizableTh width={columnWidths.projectLine} onResize={(x) => startResize('projectLine', x)}>Project Line</ResizableTh>
                          <ResizableTh width={columnWidths.taskNo} onResize={(x) => startResize('taskNo', x)}>Task No</ResizableTh>
                          <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>UOM</ResizableTh>
                          <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                            Activity
                          </ResizableTh>
                          <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                            Sub Activity
                          </ResizableTh>
                          {/* <ResizableTh width={columnWidths.product} onResize={(x) => startResize('product', x)}>Product</ResizableTh> */}
                          <ResizableTh width={columnWidths.transaction} onResize={(x) => startResize('transaction', x)}>Transaction Type</ResizableTh>
                          <ResizableTh width={columnWidths.qtyForDpr} onResize={(x) => startResize('qtyForDpr', x)} align="right">Qty Available</ResizableTh>
                          <ResizableTh width={columnWidths.qty} onResize={(x) => startResize('qty', x)} align="right">Qty</ResizableTh>
                          <ResizableTh width={columnWidths.balanceQty} onResize={(x) => startResize('balanceQty', x)} align="right">Balance</ResizableTh>
                          
                          <ResizableTh width={columnWidths.workDate} onResize={(x) => startResize('workDate', x)}>Work Date</ResizableTh>
                          <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>Remark</ResizableTh>
                          <ResizableTh width={columnWidths.action} onResize={(x) => startResize('action', x)} align="center">Action</ResizableTh>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((line, idx) => {
                           const isLineLocked =  line.BcSuccess === true || line.BcSuccess === 1;
                          return (
                          
                          <tr key={idx} className="border-t border-[#E2E8F0] hover:bg-gray-50">
                            <td className="p-2 text-center" style={{ width: columnWidths.lineNo }}>
                              <span className="text-sm font-medium">{idx + 1}</span>
                            </td>
                            <td className="p-2" style={{ width: columnWidths.projectLine }}>
                              <SearchableDropdown
                               options={filteredProjectLines}
                              value={line.ProjectLineId}
                              onChange={(val) => handleProjectLineChange(idx, val)}
                              disabled={isLineLocked}
                              placeholder={projectLinesLoading ? "Loading..." : "Select..."}
                              width="w-full"
                              isLoading={projectLinesLoading}
                              // CHANGE: Update these to show TaskNo
                              getOptionLabel={(pl) => {
                                const isDismantlable = pl.IsDismental === true || pl.IsDismental === 1;
                                const statusTag = isDismantlable ? ' [Dismantlable]' : ' [Non-Dismantlable]';
                                return `${pl.No || pl.ProductCode} (Task: ${pl.JobTaskNo || 'N/A'}) - ${pl.Description || ''} ${statusTag}`;
                              }}
                              getOptionValue={(pl) => pl.ProjectLineId}
                              getOptionSecondaryLabel={(pl) => pl.No || pl.BomItemNo || pl.ProductCode}
                              menuWidth={600}
                            />
                            </td>
                            <td className="p-2" style={{ width: columnWidths.taskNo }}>
                              <input type="text" value={line.TaskNo || ''} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 bg-gray-50 text-gray-500" />
                            </td>
                            <td className="p-2" style={{ width: columnWidths.uom }}>
                              <input type="text" value={line.UOM} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 bg-gray-50 text-gray-500 uppercase-input" />
                            </td>
                            {/* Inside the tbody map, add these two td cells */}

                            <td className="p-2" style={{ width: columnWidths.activity }}>
                              <SearchableDropdown
                                options={projectActivities.filter(a => a.IsParentActivity === 1 || a.IsParentActivity === true)}
                                value={line.ActivityId}
                                onChange={(val) => handleLineChange(idx, 'ActivityId', val)}
                                placeholder="Select Activity"
                                getOptionLabel={(a) => `${a.Code} - ${a.Description}`}
                                getOptionValue={(a) => a.ActivityId}
                                getOptionSecondaryLabel={(a) => a.Code}
                                disabled={isLineLocked}
                              />
                            </td>

                            <td className="p-2" style={{ width: columnWidths.subActivity }}>
                              {/* Filter sub-activities based on selected Activity */}
                              <SearchableDropdown
                                options={
                                  projectActivities.find(a => a.ActivityId == line.ActivityId)
                                    ? projectActivities.filter(a => a.ParentActivityCode === projectActivities.find(a => a.ActivityId == line.ActivityId).Code)
                                    : []
                                }
                                value={line.SubActivityId}
                                onChange={(val) => handleLineChange(idx, 'SubActivityId', val)}
                                placeholder="Select Sub-Activity"
                                getOptionLabel={(a) => `${a.Code} - ${a.Description}`}
                                getOptionValue={(a) => a.ActivityId}
                                getOptionSecondaryLabel={(a) => a.Code}
                                disabled={!line.ActivityId || isLineLocked} // Disabled if no parent activity
                              />
                            </td>
                            {/* <td className="p-2" style={{ width: columnWidths.product }}>
                              <input type="text" value={line.ProductCode} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 bg-gray-50 text-gray-500 uppercase-input" />
                              {line.ProductName && <div className="text-xs text-gray-500 mt-1">{line.ProductName}</div>}
                            </td> */}
                            <td className="p-2" style={{ width: columnWidths.transaction }}>
                            <select 
                              value={line.TransactionType}
                              disabled={isLineLocked}
                              onChange={e => handleTransactionTypeChange(idx, e.target.value)} 
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                            >
                              <option value="Consumption">Consumption</option>
                              <option value="Damage">Damage</option>
                              {/* Check both Header checkbox AND the specific product's IsDismental flag */}
                              {header.IsDismental && line.IsDismental && (
                                <option value="Dismental">Dismental</option>
                              )}
                            </select>
                          </td>
                           <td className="p-2" style={{ width: columnWidths.qtyForDpr }}>
                              {(!line.ActivityId || !line.SubActivityId) && line.TransactionType !== 'Dismental' ? (
                                <input 
                                  type="text" 
                                  value="Select Activity & Sub-Activity" 
                                  readOnly 
                                  className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 bg-gray-100 text-gray-400 italic text-sm"
                                />
                              ) : (
                                <input 
                                  type="number" 
                                  inputMode='decimal'
                                  step="0.000001"  
                                  value={line.TransactionType === 'Dismental' ? 0 : line.QuantityForDPR}
                                  readOnly={line.TransactionType !== 'Dismental'} 
                                  onChange={(e) => {
                                    if (line.TransactionType === 'Dismental') {
                                      e.preventDefault(); 
                                      return;
                                    }
                                    handleLineChange(idx, 'QuantityForDPR', e.target.value);
                                  }}
                                  className={`w-full border rounded-lg px-2 py-1 text-right ${
                                    line.TransactionType === 'Dismental' 
                                      ? 'bg-white border-[#E2E8F0] focus:ring-1 focus:ring-[#BDDDFC]' 
                                      : 'bg-blue-50 text-blue-700 font-semibold border-[#E2E8F0]'
                                  }`}
                                />
                              )}
                            </td>
                            <td className="p-2" style={{ width: columnWidths.qty }}>
                              <input 
                                type="number" 
                                inputMode='decimal'
                                step="0.000001" 
                                min="0" 
                                value={line.Qty}
                                onChange={e => handleQtyChange(idx, e.target.value)} 
                                onFocus={(e) => e.target.select()} 
                                className={`w-full border rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] ${
                                  line.BalanceQty < 0 ? 'border-red-500 bg-red-50' : 'border-[#E2E8F0]'
                                }`}
                                disabled={
                                  isLineLocked || 
                                  (!line.ActivityId || !line.SubActivityId) || 
                                  (line.TransactionType !== 'Dismental' && line.QuantityForDPR <= 0 && line.Qty === 0)
                                }
                              />
                              {line.BalanceQty < 0 && (
                                <div className=""></div>
                              )}
                            </td>
                            <td className="p-2" style={{ width: columnWidths.balanceQty }}>
                              <input type="number" inputMode='decimal'
                                step="0.000001" value={parseFloat(line.BalanceQty).toFixed(6)} readOnly className={`w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right font-semibold ${line.BalanceQty < 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} />
                            </td>
                            
                           {/* REPLACE THIS BLOCK */}
                            <td className="p-2" style={{ width: columnWidths.workDate }}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  value={line.WorkDate ? dayjs(line.WorkDate) : null}
                                  onChange={(newValue) => handleLineChange(idx, 'WorkDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
                                  disabled={isLineLocked}
                                  format="DD/MM/YYYY"
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      className: 'w-full'
                                    }
                                  }}
                                />
                              </LocalizationProvider>
                            </td>
                            <td className="p-2" style={{ width: columnWidths.remark }}>
                              <input type="text" value={line.Remark} onChange={e => handleLineChange(idx, 'Remark', e.target.value)} disabled={isLineLocked} placeholder="Remark..." className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" />
                            </td>
                            <td className="p-2 text-center" style={{ width: columnWidths.action }}>
                              <button type="button" className="text-red-500 hover:text-red-700 transition-colors" onClick={() => removeLine(idx)}
                               disabled={isLineLocked ||lines.length === 1}>
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {lines.length > 0 && (header.ProjectNo && header.FeederId) && (
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Lines</span>
                        <span className="text-base font-semibold">{lines.length}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Qty</span>
                        <span className="text-base font-semibold text-lg text-blue-600">
                          {lines.reduce((acc, line) => acc + (parseFloat(line.Qty) || 0), 0).toFixed(6)}
                        </span>
                      </div>
                      {/* <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Transaction Types</span>
                        <span className="text-base font-semibold">
                          {['Consumption', 'Damage', 'Dismental'].map(type => {
                            const count = lines.filter(l => l.TransactionType === type && l.Qty > 0).length;
                            return count > 0 ? `${type}: ${count}` : null;
                          }).filter(Boolean).join(', ') || 'None'}
                        </span>
                      </div> */}
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button type="button" className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={loading || isSubmitting}
                onClick={handleSubmit}
              >
                {loading || isSubmitting ? 'Saving...' : (
                  <>
                    <SaveIcon className="mr-2" size={18} />
                    {(initialData && initialData.header && initialData.header.DPRHeaderId) ? 'Update DPR' : 'Save DPR'}
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>

      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div className={`relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden ${toast.type === 'error' && 'bg-red-50/90 border-red-200'} ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}`}>
            <div className="flex gap-4 px-5 py-4">
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">{toast.title}</div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{toast.message}</div>
              </div>
            </div>
            <div className={`absolute bottom-0 left-0 h-[3px] animate-toastProgress ${toast.type === 'error' && 'bg-red-500'} ${toast.type === 'success' && 'bg-green-500'}`} />
          </div>
        </div>
      )}
    </>
  );
};

export default DPRFormModal;